import { skipToken } from "@tanstack/react-query";
import { AlertCircleIcon, SparklesIcon } from "lucide-react";
import React from "react";

import { type RouterOutputs, trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import { TextArea, TextField } from "@/components/ui/field";
import { Text } from "@/components/ui/text";
import { InsightsViewSqlButton } from "./InsightsViewSqlButton";
import { insightsLoadingMessage } from "./insights-status";

export type InsightsAskResult = RouterOutputs["admin"]["insights"]["ask"];

export type InsightsQueryPanelHandle = {
  runQuestion: (text: string) => void;
};

type InsightsQueryPanelProps = {
  onResult: (result: InsightsAskResult | null) => void;
  onFetchingChange?: (fetching: boolean) => void;
  onActiveQuestionChange?: (question: string | null) => void;
  disabled?: boolean;
};

export const InsightsQueryPanel = React.forwardRef<
  InsightsQueryPanelHandle,
  InsightsQueryPanelProps
>(function InsightsQueryPanel(
  { onResult, onFetchingChange, onActiveQuestionChange, disabled = false },
  ref,
) {
  const [question, setQuestion] = React.useState("");
  const [submittedQuestion, setSubmittedQuestion] = React.useState<
    string | null
  >(null);

  const { data: schemaInfo } = trpc.admin.insights.schema.useQuery();

  const ask = trpc.admin.insights.ask.useQuery(
    submittedQuestion ? { question: submittedQuestion } : skipToken,
    { retry: false },
  );

  React.useEffect(() => {
    onFetchingChange?.(ask.isFetching);
  }, [ask.isFetching, onFetchingChange]);

  React.useEffect(() => {
    if (ask.isFetching) {
      if (submittedQuestion) onResult(null);
      return;
    }
    if (ask.data) onResult(ask.data);
    else if (ask.error || !submittedQuestion) onResult(null);
  }, [ask.data, ask.error, ask.isFetching, submittedQuestion, onResult]);

  React.useEffect(() => {
    onActiveQuestionChange?.(submittedQuestion);
  }, [submittedQuestion, onActiveQuestionChange]);

  const bedrockConfigured = schemaInfo?.bedrockConfigured ?? true;

  const runQuestion = React.useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || ask.isFetching) return;
      setQuestion(trimmed);
      if (trimmed === submittedQuestion) {
        ask.refetch();
      } else {
        setSubmittedQuestion(trimmed);
      }
    },
    [ask, submittedQuestion],
  );

  React.useImperativeHandle(ref, () => ({ runQuestion }), [runQuestion]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    runQuestion(question);
  }

  function handleQuestionKeyDown(event: React.KeyboardEvent) {
    if (event.key !== "Enter" || event.shiftKey) return;
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      runQuestion(question);
    }
  }

  const clarification = ask.data?.clarification ?? null;
  const hint = ask.data?.hint ?? null;
  const sqlRejected = Boolean(
    ask.error && (ask.error.shape?.reason as { sql?: string } | undefined)?.sql,
  );
  const rejectedSql = sqlRejected
    ? ((ask.error?.shape?.reason as { sql?: string } | undefined)?.sql ?? null)
    : null;

  const canSubmit =
    !disabled &&
    bedrockConfigured &&
    question.trim().length > 0 &&
    !ask.isFetching;

  return (
    <div className="flex flex-col gap-2 shrink-0">
      <form onSubmit={handleSubmit} className="flex items-start gap-2">
        <TextField
          isRequired
          isDisabled={disabled || !bedrockConfigured}
          value={question}
          onChange={setQuestion}
          className="flex-1 min-w-0"
          aria-label="Insights question"
        >
          <TextArea
            rows={1}
            onKeyDown={handleQuestionKeyDown}
            placeholder="Ask a question in plain English…"
            className="min-h-9 max-h-40 resize-none field-sizing-content py-2"
          />
        </TextField>
        <Button
          type="submit"
          className="shrink-0"
          isDisabled={!canSubmit}
          isPending={ask.isFetching}
        >
          <SparklesIcon />
          Run query
        </Button>
      </form>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-h-5">
        {ask.isFetching ? (
          <Text size="2" muted>
            {insightsLoadingMessage(submittedQuestion)}
          </Text>
        ) : null}
        {!bedrockConfigured ? (
          <Text size="2" muted>
            Bedrock is not configured on the server.
          </Text>
        ) : (
          <Text size="1" muted>
            Press ⌘↵ or Ctrl↵ to run
          </Text>
        )}
      </div>

      {ask.error ? (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
          <AlertCircleIcon className="size-4 text-destructive mt-0.5 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Text>{ask.error.message}</Text>
            {rejectedSql ? (
              <InsightsViewSqlButton sql={rejectedSql} rejected />
            ) : null}
          </div>
        </div>
      ) : null}

      {clarification ? (
        <div className="rounded-lg border px-3 py-2 bg-muted/30">
          <Text size="2" muted>
            Clarification needed
          </Text>
          <Text>{clarification}</Text>
        </div>
      ) : null}

      {hint ? (
        <Text size="2" muted>
          Hint: {hint}
        </Text>
      ) : null}
    </div>
  );
});
